import { ACTIVE_COMPANY_KEY } from '../../../lib/ambientHeaders'
import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import Input from "../../../components/Input/Input"
import Button from "../../../components/Button/Button"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import Breadcrumbs from "../../../components/BreadCrumbs/Breadcrumbs"
import { fetchCompanyById, updateCompany } from "../../../features/company/company"
import { StorageGetItem, USER } from "../../../lib/storage"

const CompanySettings = () => {
  const BreadcrumbsTitleActive = ["Settings"]
  const BreadcrumbsTitle = ["Company Settings"]

  const dispatch = useDispatch()
  const { triggerLoading, triggerBar } = useMyContextLoading()

  const [companyId, setCompanyId] = useState(null)
  const [companyInfo, setCompanyInfo] = useState({
    title: '',
    qcCallSettings: {
      enabled: false,
      aiEndpoint: '',
      defaultDaysBeforeWelcome: 3
    }
  })
  const [reqError, setReqError] = useState('')

  useEffect(() => {
    const getUserCompany = async () => {
      const user = await StorageGetItem(USER)
      // The ACTIVE brand, not the caller's home firm. These two screens read and write a company
      // record; keyed on user.company they would show and edit the other firm's settings after a
      // switch, which is the one place getting it wrong writes to the wrong firm rather than merely
      // reading from it.
      const company = localStorage.getItem(ACTIVE_COMPANY_KEY) || user?.company

      if (company) {
        setCompanyId(company)
        getCompanyById(company)
      }
    }
    getUserCompany()
  }, [])

  useEffect(() => {
    const handleReqError = () => {
      reqError.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }
    handleReqError()
  }, [reqError])

  const getCompanyById = async (id) => {
    triggerBar(true)
    try {
      const response = await dispatch(fetchCompanyById(id))
      const company = response?.payload?.data
      if (company?._id) {
        setCompanyInfo({
          title: company.title || '',
          qcCallSettings: {
            enabled: company.qcCallSettings?.enabled || false,
            aiEndpoint: company.qcCallSettings?.aiEndpoint || '',
            defaultDaysBeforeWelcome: company.qcCallSettings?.defaultDaysBeforeWelcome || 3
          }
        })
      } else {
        setReqError('Failed to Get Company Information')
      }
    } catch (error) {
      setReqError(error.message || 'Failed to Get Company Information')
    }
    triggerBar(false)
  }

  const saveCompanySettings = async () => {
    if (!companyId) {
      showSnackBar("error", "Company ID not found")
      return
    }

    triggerLoading(true)
    try {
      const response = await dispatch(updateCompany({
        companyId,
        companyData: companyInfo
      }))

      if (response?.payload?.data?._id) {
        showSnackBar("success", "Company settings successfully updated")
      } else {
        setReqError('Failed to Update Company Settings')
      }
    } catch (error) {
      setReqError(error.message || 'Failed to Update Company Settings')
    }
    triggerLoading(false)
  }

  const handleQCSettingChange = ({ name, value }) => {
    setCompanyInfo(prev => ({
      ...prev,
      qcCallSettings: {
        ...prev.qcCallSettings,
        [name]: value
      }
    }))
  }

  return (
    <div>
      <div>
        <h1 className="font-bold text-xl mb-2 w-full">
          Company Settings
        </h1>
        <Breadcrumbs title={BreadcrumbsTitle} titleActive={BreadcrumbsTitleActive} />
      </div>

      <div className="mt-10 px-6 py-5 rounded-2xl shadow-lg bg-surface border border-divider">
        <div className="flex flex-col gap-6">
          {/* Editable, which it was not. The field carried an onChange that could never fire, because
              the input was hardcoded disabled — a leftover from the single-brand system, where the
              company name was cosmetic and nobody was meant to change it.

              With two firms it is not cosmetic: this string is what the Select Brand picker renders,
              so a firm with an empty name is a blank button an operator has to click blind. That is
              the state MDL is in on stage right now, and there was no way to fix it through the
              product. The API has always accepted `title` on this endpoint, and the endpoint is
              self-scoped, so nothing else had to change. */}
          <Input
            placeholder="Company Name"
            label="Company Name"
            value={companyInfo?.title}
            onChange={(e) => setCompanyInfo(prev => ({ ...prev, title: e.target.value }))}
          />

          {/* QC Call Settings */}
          <div className="border-t border-divider pt-6 mt-2">
            <h3 className="text-lg font-semibold mb-4">Quality Control Call Integration</h3>

            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="qcCallEnabled"
                checked={companyInfo?.qcCallSettings?.enabled}
                onChange={(e) => handleQCSettingChange({ name: 'enabled', value: e.target.checked })}
                className="w-5 h-5 cursor-pointer"
              />
              <label htmlFor="qcCallEnabled" className="text-sm font-medium cursor-pointer">
                Enable AI-powered Quality Control Call (Company-wide)
              </label>
            </div>

            {companyInfo?.qcCallSettings?.enabled && (
              <div className="ml-8 mt-4 flex flex-col gap-4">
                <div>
                  <Input
                    placeholder="https://api.example.com/qc-call"
                    label="AI Endpoint URL (Read-Only)"
                    value={companyInfo?.qcCallSettings?.aiEndpoint}
                    disabled
                  />
                  <p className="text-text-secondary text-sm mt-2">
                    AI endpoint is configured via environment variables and cannot be changed here for security reasons.
                  </p>
                </div>

                <div>
                  <Input
                    placeholder="3"
                    label="Default Days before Welcome Call (3-5 days)"
                    type="number"
                    min="3"
                    max="5"
                    value={companyInfo?.qcCallSettings?.defaultDaysBeforeWelcome}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10)
                      if (!isNaN(val) && val >= 3 && val <= 5) {
                        handleQCSettingChange({ name: 'defaultDaysBeforeWelcome', value: val })
                      }
                    }}
                  />
                  <p className="text-text-secondary text-sm mt-2">
                    This is the default delay for all Contact Groups. Individual Contact Groups can override this setting.
                  </p>
                </div>

                <div className="bg-primary-main/10 p-4 rounded-lg">
                  <h4 className="font-semibold text-sm mb-2">How it works:</h4>
                  <ul className="text-sm text-text-secondary space-y-1 list-disc list-inside">
                    <li>Contact Group settings take priority over Company settings</li>
                    <li>When enabled, clients remain Inactive until QC call completes</li>
                    <li>AI performs automated quality control call</li>
                    <li>On success: client is activated and Welcome Call is scheduled</li>
                    <li>On failure: sales rep is notified via email</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end items-center gap-4 self-stretch py-6 border-t border-divider mt-6">
          <Button size="lg" width="w-[156px]" onClick={saveCompanySettings}>
            Save
          </Button>
        </div>

      </div>
    </div>
  )
}

export default CompanySettings
